import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { NodeEdgeMapper } from '@iotv/datamodel';
import { AdjacentType, AppValueTypes } from '@iotv/iotv-v3-types';
import { Grid, Stack } from '@mui/material';
import React, { useCallback, useContext, useEffect, useReducer, useRef, useState } from 'react';
import { AppMainViewContext } from '../../../ContextProviders/AppContextProvider';
import { getNodeEdgeMapperReducer } from '../../../ContextProviders/NodeEdgeMapperComponentContext';
import { GoogleMapAPIMapTypeIds } from '../../../GoogleMapV4';
import { useThemedMap } from '../../../GoogleMapV4/useThemedMap';
import { ObjectViewType } from '../../../types';
import { SimpleList } from '../../factories/Listerlizer/SimpleList';
import { getGatewayReceptionLineStyleFn } from '../../NetworkCoverage/Map/FeatureStyles';
import { NetworkCoverageContext } from '../../NetworkCoverage/NetworkCoverageContext';
const entityViewDefinition = {
    name: {
        key: 'name',
        label: 'Name',
        type: AppValueTypes.STRING,
        editable: true,
        unit: undefined,
        precision: undefined,
        stateFn: undefined,
        required: true,
        validationRx: undefined,
    },
    eui: {
        key: 'eui',
        label: 'EUI',
        type: AppValueTypes.STRING,
        editable: true,
        unit: undefined,
        precision: undefined,
        stateFn: undefined,
        required: true,
        validationRx: undefined,
    },
};
const viewDefinitionColumnVisibility = Object.fromEntries(Object.keys(entityViewDefinition ?? {}).map((k) => [k, true]));
const adjacentFilter = {
    adjacentType: AdjacentType.CHILD,
    objectType: 'Device',
};
const columnVisibilityReferences = {
    dataTableReference: 'ListCardV1',
    objectTypeId: adjacentFilter.objectType,
    viewType: ObjectViewType.List,
    externalColumnVisibility: viewDefinitionColumnVisibility,
};
const InvestigationDeviceMap = (props) => {
    const { devices: preDevices, gateways } = props;
    const [hash, setHash] = useState('');
    useEffect(() => {
        const selectedDevicesHash = preDevices.map((t) => t.id).join();
        setHash(selectedDevicesHash);
    }, [preDevices]);
    const devices = preDevices.map((dev) => {
        return {
            ...dev,
            name: dev.eui ? dev.eui.substring(dev.eui.length - 5) : dev.id,
        };
    });
    const viewObject = {
        matchedPrimary: undefined,
        matchedRelatedByPk: [],
        matchedRelatedBySk: devices,
    };
    const [{ TransactionFunctions, UserFunctions }] = useContext(AppMainViewContext);
    const nodeEdgeReducer = getNodeEdgeMapperReducer({ canaryString: 'InvestigationDeviceMap' });
    const [state, dispatch] = useReducer(nodeEdgeReducer, {
        nodeEdgeMapper: new NodeEdgeMapper(),
        nemHash: null,
        apexNode: null,
    });
    const options = {
        circleOptions: {
            fillColor: '#ffff00',
            fillOpacity: 1,
            strokeWeight: 5,
            clickable: false,
            editable: true,
            zIndex: 1,
        },
        polylineOptions: {
            strokeColor: '#ffff00',
            strokeOpacity: 1,
            strokeWeight: 1,
            clickable: false,
            editable: true,
            zIndex: 1,
        },
        polygonOptions: {
            zIndex: 1,
            strokeColor: '#ffff00',
            strokeOpacity: 1,
            strokeWeight: 1,
            clickable: false,
            editable: true,
        },
    };
    const { state: networkCoverageState } = useContext(NetworkCoverageContext);
    const { lineColorMetricAttribute } = networkCoverageState;
    const pathDataAttributeKeyRef = useRef(lineColorMetricAttribute);
    const lineColorFn = useRef(getGatewayReceptionLineStyleFn(pathDataAttributeKeyRef.current));
    const getUseThemedMapInput = useCallback(() => {
        const res = {
            canaryString: 'EffluentPathMap',
            contextObject: null,
            nodeEdgeMapper: state.nodeEdgeMapper,
            nodeEdgeMapperDispatch: dispatch,
            transactionFunctions: TransactionFunctions,
            options: {
                mapTypeId: GoogleMapAPIMapTypeIds.terrain,
                mapTypeControlOptions: {
                    position: google.maps.ControlPosition.BOTTOM_RIGHT,
                },
                controlSize: 24,
            },
            dataFn: (async (gob) => {
                return { ...gob, data: {} };
            }),
            functions: {},
            showDrawingTools: false,
            themesAndLabels: {},
            drawingManagerOptions: options,
            /** areaData is mutated in useThemedMap props */
            areaData: [],
            /** lineDefiningPairs is mutated in useThemedMap props */
            lineDefiningPairs: [],
        };
        return res;
    }, [state, devices]);
    const { map, components: { PortalDiv, MapDatalayerVisibility, MapLegend }, state: stateWithData, } = useThemedMap(getUseThemedMapInput());
    useEffect(() => {
        let loadAll = [...devices];
        // get relevant gateways
        const allGatewayId = [...new Set(devices.map((device) => device.lastGatewayEui).filter((val) => val !== undefined))];
        const gatewaysFiltered = gateways.filter((gateway) => allGatewayId.includes(gateway.id));
        loadAll = [...loadAll, ...gatewaysFiltered];
        // generate lines
        const pairing = devices
            .map((dev) => {
            // matched gateway
            const foundGateList = gatewaysFiltered.filter((gate) => gate.id === dev.lastGatewayEui);
            if (foundGateList.length === 1) {
                const foundGate = foundGateList[0];
                const id = `${dev.id}_${foundGate.id}`;
                const sk = `Path:${id}`;
                return {
                    pk: sk,
                    sk: sk,
                    id,
                    type: 'Path',
                    points: [dev.location, foundGate.location],
                };
            }
        })
            .filter((val) => val !== undefined);
        loadAll = [...loadAll, ...pairing];
        dispatch({ type: 'loadItems', value: { items: loadAll } });
    }, [hash]);
    return (_jsx("div", { children: _jsxs(Grid, { container: true, children: [_jsx(Grid, { sx: {
                        height: '60vh',
                    }, size: 4, children: _jsx(SimpleList, { listItems: devices, viewDefinition: entityViewDefinition }) }), _jsxs(Grid, { size: 8, children: [_jsx(Stack, { sx: { position: 'relative', top: 0, left: 0 }, direction: 'column', height: '60vh', children: map.current }), PortalDiv] })] }) }));
};
export default InvestigationDeviceMap;
